import { Button } from '@99designs/common/components/Button';
import { Pill } from '@99designs/common/components/Pill';
import { DesignerMeta_User } from '__generated__/DesignerMeta_User';
import classNames from 'classnames';
import Icon from 'launch/components/Icon';
import Avatar from 'projects/components/Avatar';
import LocalTime from 'projects/components/LocalTime';
import OnlineStatus from 'projects/components/OnlineStatus';
import StatsTable from 'projects/components/StatsTable';
import { isDesignerOnline } from 'projects/util/designer';
import * as React from 'react';

export interface Props {
    designer: DesignerMeta_User;
    onRemove?: (userId: number) => void;
    hasFullStats?: boolean;
    onClickDescription?: () => void;
    showFullBiography?: boolean;
    isCompact?: boolean;
    highlightNewDesigners?: boolean;
    displayStatsTable?: boolean;
}

const DesignerMeta = ({
    designer,
    hasFullStats = true,
    onRemove,
    onClickDescription,
    showFullBiography = false,
    isCompact = false,
    highlightNewDesigners = true,
    displayStatsTable = true,
}: Props) => {
    const isOnlineNow = isDesignerOnline(designer);
    const showStatsTable = displayStatsTable && designer.profile.completedProjectsCount > 0;

    return (
        <div
            className={classNames({
                'designer-meta': true,
                'designer-meta--minimal': !hasFullStats,
            })}
        >
            <div className="designer-meta__header">
                <div className="designer-meta__header__avatar">
                    <Avatar
                        userId={designer.id}
                        variations={isCompact ? ['extra-small'] : ['small']}
                        isOnline={isOnlineNow}
                        onRemove={onRemove}
                    />
                </div>
                <div className="designer-meta__header__name">
                    <div className="paragraph paragraph--regular paragraph--no-margin">
                        {designer.displayName}
                    </div>
                </div>
                <div className="designer-meta__header__online-status">
                    <div
                        className={classNames({
                            paragraph: true,
                            'paragraph--no-margin': true,
                            'paragraph--regular': !isCompact,
                            'paragraph--small': isCompact,
                        })}
                    >
                        <OnlineStatus
                            isOnline={isOnlineNow}
                            lastSeenAt={designer.profile.lastSeenAt}
                        />
                    </div>
                </div>
                <div className="designer-meta__header__local-time">
                    <div
                        className={classNames({
                            paragraph: true,
                            'paragraph--no-margin': true,
                            'paragraph--regular': !isCompact,
                            'paragraph--small': isCompact,
                        })}
                    >
                        <LocalTime utcOffset={designer.profile.utcOffset} />
                    </div>
                </div>
            </div>
            {hasFullStats && (
                <>
                    {showStatsTable && (
                        <div className="designer-meta__responsiveness">
                            <StatsTable>
                                <StatsTable.Stat
                                    amount={designer.profile.completedProjectsCount}
                                    label={__('Projects')}
                                />
                                <StatsTable.Stat
                                    amount={designer.profile.repeatCount}
                                    label={__('Repeat clients')}
                                />
                                <StatsTable.Stat
                                    amount={`${designer.profile.responsivenessScore}%`}
                                    label={__('Responds within 24 hours')}
                                />
                            </StatsTable>
                        </div>
                    )}
                    {!showStatsTable && highlightNewDesigners && (
                        <div className="designer-meta__new">
                            <div className="designer-meta__new__pill">
                                <Pill variations={['custom']}>
                                    <Icon icon="star-2" /> {__("I'm new here")}
                                </Pill>
                            </div>
                            <div className="paragraph paragraph--small paragraph--soft paragraph--no-margin">
                                {__('Be one of the first to work with this designer')}
                            </div>
                        </div>
                    )}
                    {/* TODO: ellipsis not showing */}
                    <div className="designer-meta__bio">
                        {designer.profile.biography.length > 0 && (
                            <>
                                <div
                                    className={classNames({
                                        'designer-meta-bio': true,
                                        'designer-meta-bio--expanded': showFullBiography,
                                    })}
                                >
                                    <div className="paragraph paragraph--small paragraph--no-margin">
                                        {designer.profile.biography}
                                    </div>
                                </div>
                                <div className="designer-meta__bio__link">
                                    <Button variations={['link']} onClick={onClickDescription}>
                                        <div className="paragraph paragraph--small paragraph--no-margin">
                                            {__('Full description')}
                                        </div>
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
export default DesignerMeta;
